// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { AppEvent } from '@interfaces/interfaces';
import { AccountType } from '@interfaces/user.interfaces';
import { NavService } from '@services/nav.service';
import { UserService } from '@services/user.service';
import { takeWhile } from 'rxjs';
import { tenantIsStellantis } from 'src/app/utils/utils';
import { isMobileOrTablet } from 'src/app/utils/utils';
import { DealershipService } from '../../services/dealership.service';
import { PreloadService } from '@services/preload.service';

@Component({
    selector: 'dealer-dashboard',
    templateUrl: './dealer-dashboard.component.html',
    styleUrls: ['./dealer-dashboard.component.scss'],
})
export class DealerDashboardComponent implements OnInit, OnDestroy {
    public hideSideNav = false;
    private isComponentAlive = true;
    public isLoading = false;
    public hideFooter = false;
    protected hideBreadcrumbs = false;
    public versionNumber: string = null;
    public helpPanel$ = this.navService.helpPanel$;
    public isDesktop: boolean;
    public isCollapsed: boolean;
    constructor(
        private readonly router: Router,
        private readonly navService: NavService,
        private readonly userService: UserService,
        private readonly route: ActivatedRoute,
        private readonly dealershipService: DealershipService,
        private preloadService: PreloadService
    ) {
        this.dealershipService.dealer$.pipe(takeWhile(() => this.isComponentAlive)).subscribe((dealer) => {
            this.navService.accountType = tenantIsStellantis(dealer.oemCode) ? AccountType.stellantisDealer : AccountType.ddjDealer;
        });
    }

    ngOnInit(): void {
        this.navService.navState$.pipe(takeWhile(() => this.isComponentAlive)).subscribe((state) => {
            this.isCollapsed = state === 'collapsed';
        });

        this.preloadResources();
        if (!isMobileOrTablet()) {
            this.isDesktop = true;
        } else {
            this.isDesktop = false;
        }
        // get most recent version number from resolver for footer
        if ((this.route.snapshot.data?.version || []).length > 0) {
            this.versionNumber = this.route.snapshot.data?.version[0]?.versionNumber;
        }

        // need to check when page is refreshed
        this.hideFooter = window.location.pathname.includes('/sign');
        this.hideBreadcrumbs = window.location.pathname.includes('/gettingstarted');

        // set selected nav item
        const url: string[] = window.location.pathname.split('/').splice(2);
        this.navService.setSelectedRouteByURL(url.join('/'));

        this.router.events.pipe(takeWhile(() => this.isComponentAlive)).subscribe((val) => {
            if (val instanceof NavigationEnd) {
                let url: string[] = val.url.split('/').splice(2);

                // if they just try to hit dashboard - send to the deals page
                if (url.length === 0) {
                    url = AccountType.ddjDealer ? ['deals'] : ['eshop'];
                    this.router.navigate(['/dashboard', url[0]]);
                }

                // need to check when router events fire, not just on load of dashboard
                if (val.url.includes('sign')) {
                    this.hideFooter = window.location.pathname.includes('/sign');
                }

                this.hideBreadcrumbs = val.url.includes('gettingstarted');

                this.navService.setSelectedRouteByURL(url.join('/'));
            }
        });

        this.userService.checkAndHandleMissingSignature();
    }

    preloadResources(): void {
        this.preloadService.preloadDealerships();
    }

    public eventHandler(event: AppEvent): void {
        if (event.context === 'loader') {
            this.isLoading = event.data;
        }
    }

    ngOnDestroy(): void {
        this.isComponentAlive = false;
    }
}
